import { useEffect, useState } from "react";

interface GeolocationResponse {
	success: boolean;
	countryCode: string;
	country: string;
	city?: string;
	error?: string;
}

interface UseCountryDetectionResult {
	countryCode: string;
	country: string;
	city: string;
	isLoading: boolean;
	error: string | null;
}

const CACHE_KEY = "era_detected_location_v2"; // Changed key to invalidate old cache
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Custom hook to detect user's country and city based on IP address
 * Uses sessionStorage to cache the result and avoid repeated API calls
 * Defaults to "us" if detection fails
 */
export function useCountryDetection(): UseCountryDetectionResult {
	const [countryCode, setCountryCode] = useState<string>("us");
	const [country, setCountry] = useState<string>("");
	const [city, setCity] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const detectCountry = async () => {
			try {
				// Check sessionStorage cache first
				const cached = sessionStorage.getItem(CACHE_KEY);
				if (cached) {
					try {
						const {
							countryCode: cachedCode,
							country: cachedCountry,
							city: cachedCity,
							timestamp,
						} = JSON.parse(cached);
						const age = Date.now() - timestamp;

						// Use cached value if less than 24 hours old
						if (age < CACHE_DURATION && cachedCode) {
							console.log(
								`[Country Detection] Using cached location: ${cachedCity || ""}, ${cachedCountry || cachedCode}`,
							);
							setCountryCode(cachedCode.toLowerCase());
							setCountry(cachedCountry || "");
							setCity(cachedCity || "");
							setIsLoading(false);
							return;
						}
					} catch (parseError) {
						console.warn(
							"[Country Detection] Failed to parse cached country:",
							parseError,
						);
						sessionStorage.removeItem(CACHE_KEY);
					}
				}

				// Cache miss or expired - fetch from API
				console.log("[Country Detection] Fetching country from API...");

				const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
				const response = await fetch(`${apiUrl}/api/geolocation`, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				});

				if (!response.ok) {
					throw new Error(`API request failed: ${response.statusText}`);
				}

				const data: GeolocationResponse = await response.json();

				if (data.success && data.countryCode) {
					const detectedCode = data.countryCode.toLowerCase();
					const detectedCountry = data.country || "";
					const detectedCity = data.city || "";

					console.log(
						`[Country Detection] Detected location: ${detectedCity ? `${detectedCity}, ` : ""}${detectedCountry} (${detectedCode})`,
					);

					// Cache the result
					sessionStorage.setItem(
						CACHE_KEY,
						JSON.stringify({
							countryCode: detectedCode,
							country: detectedCountry,
							city: detectedCity,
							timestamp: Date.now(),
						}),
					);

					setCountryCode(detectedCode);
					setCountry(detectedCountry);
					setCity(detectedCity);
				} else {
					throw new Error(
						data.error || "Failed to detect country from response",
					);
				}
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Unknown error";
				console.warn(
					`[Country Detection] Error detecting country: ${errorMessage}. Defaulting to US.`,
				);
				setError(errorMessage);
				// Keep default "us" on error
			} finally {
				setIsLoading(false);
			}
		};

		detectCountry();
	}, []);

	return { countryCode, country, city, isLoading, error };
}
